import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AccountBalanceWallet as CashIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { generateCashFlowReport } from '../services/reportService';

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getInitialFilters = () => {
  const now = new Date();
  return {
    startDate: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: toDateInput(now),
  };
};

const getInitialTransaction = (direction = 'incoming') => ({
  direction,
  category: direction === 'incoming' ? 'sale' : 'expenses',
  amount: '',
  description: '',
  date: toDateInput(new Date()),
});

const money = (value) => `€${Number(value || 0).toFixed(2)}`;
const formatDate = (value) => value ? new Date(value).toLocaleDateString() : 'N/A';

const CashFlowReportViewer = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(getInitialFilters);
  const [report, setReport] = useState(null);
  const [manualTransactions, setManualTransactions] = useState([]);
  const [transactionDialog, setTransactionDialog] = useState(null);
  const [transactionForm, setTransactionForm] = useState(getInitialTransaction);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setFilters(getInitialFilters());
      setReport(null);
      setManualTransactions([]);
      setTransactionDialog(null);
    }
  }, [open]);

  const handleChange = (field) => (event) => {
    setFilters((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleQuickFilter = (type) => {
    const now = new Date();
    let startDate;
    let endDate;

    switch (type) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = startDate;
        break;
      case 'yesterday': {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        endDate = startDate;
        break;
      }
      case 'currentMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = now;
        break;
      case 'lastMonth':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'currentQuarter': {
        const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
        startDate = new Date(now.getFullYear(), quarterStartMonth, 1);
        endDate = now;
        break;
      }
      default:
        return;
    }

    setFilters((current) => ({
      ...current,
      startDate: toDateInput(startDate),
      endDate: toDateInput(endDate),
    }));
  };

  const handleTransactionChange = (field) => (event) => {
    setTransactionForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const openTransactionDialog = (direction) => {
    setTransactionForm(getInitialTransaction(direction));
    setTransactionDialog(direction);
  };

  const closeTransactionDialog = () => setTransactionDialog(null);

  const handleSaveTransaction = () => {
    if (!transactionForm.amount || Number(transactionForm.amount) <= 0 || !transactionForm.description.trim() || !transactionForm.date) {
      toast.error(t('cash_flow_transaction_required'));
      return;
    }

    setManualTransactions((current) => [
      ...current,
      {
        ...transactionForm,
        amount: Number(transactionForm.amount),
      },
    ]);
    closeTransactionDialog();
  };

  const handleGenerate = async () => {
    if (!filters.startDate || !filters.endDate || filters.startDate > filters.endDate) {
      toast.error(t('cash_flow_invalid_date_range'));
      return;
    }

    try {
      setLoading(true);
      const data = await generateCashFlowReport({
        startDate: `${filters.startDate}T00:00:00.000`,
        endDate: `${filters.endDate}T23:59:59.999`,
      });
      setReport(data);
    } catch (error) {
      toast.error(error.response?.data?.message || t('cash_flow_generation_failed'));
    } finally {
      setLoading(false);
    }
  };

  const openingBalance = 0;
  const manualIncoming = manualTransactions
    .filter((transaction) => transaction.direction === 'incoming')
    .reduce((total, transaction) => total + transaction.amount, 0);
  const manualOutgoing = manualTransactions
    .filter((transaction) => transaction.direction === 'outgoing')
    .reduce((total, transaction) => total + transaction.amount, 0);
  const incomingTotal = (report?.incoming || 0) + manualIncoming;
  const outgoingTotal = (report?.outgoing || 0) + manualOutgoing;
  const netCashFlow = incomingTotal - outgoingTotal;
  const closingBalance = openingBalance + netCashFlow;
  const hasCashFlow = Boolean(report) || manualTransactions.length > 0;
  const summaryCardSx = {
    height: '100%',
    minHeight: 132,
    display: 'flex',
    alignItems: 'stretch',
  };
  const summaryCardContentSx = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CashIcon color="success" />
          <Typography variant="h6" fontWeight={700}>{t('cash_flow_report')}</Typography>
        </Box>
        <IconButton onClick={onClose} aria-label={t('close')}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={() => handleQuickFilter('today')}>
            {t('today')}
          </Button>
          <Button size="small" variant="outlined" onClick={() => handleQuickFilter('yesterday')}>
            {t('yesterday')}
          </Button>
          <Button size="small" variant="outlined" onClick={() => handleQuickFilter('currentMonth')}>
            {t('current_month')}
          </Button>
          <Button size="small" variant="outlined" onClick={() => handleQuickFilter('lastMonth')}>
            {t('last_month')}
          </Button>
          <Button size="small" variant="outlined" onClick={() => handleQuickFilter('currentQuarter')}>
            {t('current_quarter')}
          </Button>
        </Box>

        <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="date" label={t('from')} value={filters.startDate} onChange={handleChange('startDate')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="date" label={t('to')} value={filters.endDate} onChange={handleChange('endDate')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              sx={{ height: '56px' }}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? t('generating') : t('generate_report')}
            </Button>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="outlined" color="success" startIcon={<AddIcon />} onClick={() => openTransactionDialog('incoming')}>
              {t('create_incoming_transaction')}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button fullWidth variant="outlined" color="error" startIcon={<RemoveIcon />} onClick={() => openTransactionDialog('outgoing')}>
              {t('create_outgoing_transaction')}
            </Button>
          </Grid>
        </Grid>

        {hasCashFlow && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
              <Card sx={{ ...summaryCardSx, bgcolor: '#f3e5f5' }}><CardContent sx={summaryCardContentSx}><Typography color="text.secondary">{t('opening_balance')}</Typography><Typography variant="h5" fontWeight={700} color="secondary.dark">{money(openingBalance)}</Typography></CardContent></Card>
              <Card sx={{ ...summaryCardSx, bgcolor: '#e8f5e9' }}><CardContent sx={summaryCardContentSx}><Typography color="text.secondary">{t('cash_sales')}</Typography><Typography variant="h5" fontWeight={700} color="success.dark">{money(incomingTotal)}</Typography></CardContent></Card>
              <Card sx={{ ...summaryCardSx, bgcolor: '#ffebee' }}><CardContent sx={summaryCardContentSx}><Typography color="text.secondary">{t('paid_cash_purchases')}</Typography><Typography variant="h5" fontWeight={700} color="error.dark">{money(outgoingTotal)}</Typography></CardContent></Card>
              <Card sx={{ ...summaryCardSx, bgcolor: '#fff3e0' }}><CardContent sx={summaryCardContentSx}><Typography color="text.secondary">{t('net_cash_flow')}</Typography><Typography variant="h5" fontWeight={700} color={netCashFlow >= 0 ? 'success.dark' : 'error.dark'}>{money(netCashFlow)}</Typography></CardContent></Card>
              <Card sx={{ ...summaryCardSx, bgcolor: closingBalance >= 0 ? '#e3f2fd' : '#fff3e0' }}><CardContent sx={summaryCardContentSx}><Typography color="text.secondary">{t('closing_cash_in_hand')}</Typography><Typography variant="h5" fontWeight={700}>{money(closingBalance)}</Typography><Typography variant="caption">{t('opening_balance_plus_net_flow')}</Typography></CardContent></Card>
            </Box>

            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{t('paid_cash_purchases')}</Typography>
            {(report?.purchases || []).length === 0 ? <Typography color="text.secondary">{t('no_paid_cash_purchases')}</Typography> : report.purchases.map((purchase) => (
              <Box key={`${purchase.invoiceNumber}-${purchase.date}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box><Typography variant="body2" fontWeight={600}>{purchase.supplierName}</Typography><Typography variant="caption" color="text.secondary">{purchase.invoiceNumber} · {formatDate(purchase.date)}</Typography></Box>
                <Typography fontWeight={700} color="error.main">-{money(purchase.amount)}</Typography>
              </Box>
            ))}
            {manualTransactions.map((transaction, index) => (
              <Box key={`${transaction.date}-${index}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{transaction.description}</Typography>
                  <Typography variant="caption" color="text.secondary">{t(transaction.category)} · {formatDate(transaction.date)}</Typography>
                </Box>
                <Typography fontWeight={700} color={transaction.direction === 'incoming' ? 'success.main' : 'error.main'}>
                  {transaction.direction === 'incoming' ? '+' : '-'}{money(transaction.amount)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t('close')}</Button></DialogActions>

      <Dialog open={Boolean(transactionDialog)} onClose={closeTransactionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{transactionDialog === 'incoming' ? t('create_incoming_transaction') : t('create_outgoing_transaction')}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label={t('amount')}
                value={transactionForm.amount}
                onChange={handleTransactionChange('amount')}
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      €
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required type="date" label={t('date')} value={transactionForm.date} onChange={handleTransactionChange('date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required select label={t('transaction_category')} value={transactionForm.category} onChange={handleTransactionChange('category')}>
                {transactionDialog === 'incoming' ? (
                  <>
                    <MenuItem value="sale">{t('sale')}</MenuItem>
                    <MenuItem value="other_income">{t('other_income')}</MenuItem>
                  </>
                ) : (
                  <>
                    <MenuItem value="expenses">{t('expenses')}</MenuItem>
                    <MenuItem value="purchase">{t('purchase')}</MenuItem>
                  </>
                )}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required multiline minRows={3} label={t('description')} value={transactionForm.description} onChange={handleTransactionChange('description')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTransactionDialog}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleSaveTransaction}>{t('save')}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CashFlowReportViewer;